import type { BoardProperties } from "@/types/board";

/**
 * Referential validation for stored property/option ids.
 *
 * Pages store references by id — `value.databaseProperties[propertyId]` holds a
 * property id whose value is an option id (or array of them) for option types.
 * The datasource schema is the source of truth; those ids can go stale when a
 * property or option is deleted. These predicates are the single place that
 * decides whether a stored reference is still live, so renderers can drop dead
 * refs and filter/sort/group logic can skip them instead of silently hiding
 * rows or showing raw ids.
 */

/** Property types whose stored value(s) reference option ids. */
const OPTION_TYPES = new Set(["status", "select", "multi_select", "priority"]);

export function isOptionType(type?: string | null): boolean {
  return !!type && OPTION_TYPES.has(type);
}

/** True when the datasource schema still defines this property. */
export function isLiveProperty(
  properties: BoardProperties | null | undefined,
  propertyId: string,
): boolean {
  return !!properties && !!properties[propertyId];
}

type WithOptions = { options?: { id?: string }[] } | null | undefined;

/**
 * True when `optionId` is still defined by the property's options. When the
 * options aren't loaded yet (absent or empty) we cannot prove the id is dead,
 * so it is treated as live — this avoids dropping values during the load
 * window before the datasource has hydrated.
 */
export function isLiveOptionId(property: WithOptions, optionId: string): boolean {
  const options = property?.options;
  if (!Array.isArray(options) || options.length === 0) return true;
  return options.some((o) => o && String(o.id) === String(optionId));
}

type WithGroups = { groups?: { id?: string }[] } | null | undefined;

/**
 * True when `groupId` is still defined by the property's status groups. Unlike
 * options, absent groups means genuinely ungrouped rather than not-yet-loaded,
 * so an unmatched id here is dead rather than merely unproven.
 */
export function isLiveGroupId(property: WithGroups, groupId: string): boolean {
  const groups = property?.groups;
  if (!Array.isArray(groups)) return false;
  return groups.some((g) => g && String(g.id) === String(groupId));
}

/** Keep only the option ids the property still defines, preserving order. */
export function keepLiveOptionIds(property: WithOptions, ids: string[]): string[] {
  return ids.filter((id) => isLiveOptionId(property, id));
}
